import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';

// ==============================
// API endpoints – غيّر الـ base URL حسب مشروعك
// ==============================
const API_BASE = 'https://api.cleany.qa/api/v1/admin';

async function getRegions(params: { page: number }) {
  const query = new URLSearchParams({ page: String(params.page) });
  const res = await fetch(`${API_BASE}/regions?${query}`);
  const json = await res.json();
  const list = json.data ?? json.list ?? [];
  const meta = json.meta ?? null;
  return { list, meta };
}

async function createRegion(data: FormData | Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/regions`, {
    method: 'POST',
    headers:
      data instanceof FormData ? {} : { 'Content-Type': 'application/json' },
    body: data instanceof FormData ? data : JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Create failed');
  return res.json();
}

async function updateRegion(id: number, data: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/regions/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

async function deleteRegion(id: number) {
  const res = await fetch(`${API_BASE}/regions/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Delete failed');
  return res.json();
}

// ==============================
// Types
// ==============================
type Region = {
  id: number;
  name: string;
  is_active: number;
  created_at?: string;
  updated_at?: string;
};

type RegionFormState = {
  name: string;
  is_active: number;
};

const emptyForm: RegionFormState = {
  name: '',
  is_active: 1,
};

// ==============================
// SweetAlert2 helpers
// ==============================

const showSuccess = (msg: string) =>
  Swal.fire({
    icon: 'success',
    title: msg,
    timer: 2000,
    showConfirmButton: false,
    toast: true,
    position: 'top-end',
    background: '#1a1a2e',
    color: '#fff',
    iconColor: '#4ade80',
  });

const showError = (msg: string) =>
  Swal.fire({
    icon: 'error',
    title: 'Oops!',
    text: msg,
    toast: true,
    position: 'top-end',
    timer: 3000,
    showConfirmButton: false,
    background: '#1a1a2e',
    color: '#fff',
    iconColor: '#f87171',
  });

const confirmDelete = async (msg: string): Promise<boolean> => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: msg,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel',
    background: '#1a1a2e',
    color: '#fff',
    iconColor: '#facc15',
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    customClass: {
      popup: 'swal-custom-popup',
      confirmButton: 'swal-confirm-btn',
      cancelButton: 'swal-cancel-btn',
    },
  });
  return result.isConfirmed;
};

// ==============================
// Component
// ==============================
const Regions: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formState, setFormState] = useState<RegionFormState>(emptyForm);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // ---- fetch ----
  const fetchRegions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const { list, meta } = await getRegions({ page });
      setRegions(list as Region[]);
      if (meta) {
        setCurrentPage(meta.current_page);
        setTotalPages(meta.last_page);
      }
    } catch {
      showError('Failed to fetch regions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegions(1);
  }, [fetchRegions]);

  // ---- filtered list ----
  const filteredRegions = regions.filter((r) => {
    const matchSearch = r.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchStatus =
      filterStatus === 'all'
        ? true
        : filterStatus === 'active'
        ? r.is_active === 1
        : r.is_active === 0;
    return matchSearch && matchStatus;
  });

  // ---- form handlers ----
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: name === 'is_active' ? Number(value) : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formState.name.trim()) newErrors.name = 'Region name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: formState.name,
      is_active: formState.is_active,
    };

    try {
      if (editingId === null) {
        await createRegion(payload);
        showSuccess('Region Created');
      } else {
        await updateRegion(editingId, payload);
        showSuccess('Region Updated');
      }
      setIsModalOpen(false);
      fetchRegions(currentPage);
    } catch {
      showError('Submission Failed');
    }
  };

  const handleDelete = async (region: Region) => {
    const confirmed = await confirmDelete(`Delete "${region.name}"?`);
    if (confirmed) {
      try {
        await deleteRegion(region.id);
        showSuccess('Deleted successfully');
        fetchRegions(currentPage);
      } catch {
        showError('Deletion failed');
      }
    }
  };

  const openEdit = (region: Region) => {
    setEditingId(region.id);
    setFormState({
      name: region.name || '',
      is_active: region.is_active,
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormState(emptyForm);
    setErrors({});
    setIsModalOpen(true);
  };

  // ---- pagination ----
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="crud-pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => fetchRegions(currentPage - 1)}
        >
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => fetchRegions(currentPage + 1)}
        >
          Next
        </button>
      </div>
    );
  };

  // ==============================
  // Render
  // ==============================
  return (
    <section className="cyber-section-card">
      {/* Header */}
      <div className="crud-page-header">
        <div>
          <p className="cyber-page-kicker">Management</p>
          <h1 className="cyber-standalone-title">Regions</h1>
        </div>
        <button className="crud-add-button" onClick={openCreate}>
          + Create Region
        </button>
      </div>

      {/* Filters */}
      <div className="crud-filters-row">
        <input
          type="text"
          placeholder="Search regions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid var(--color-border, #ccc)',
            background: 'var(--color-surface, #fff)',
            color: 'var(--color-text, #000)',
            minWidth: '220px',
          }}
        />
        <div className="filter-item">
          <span>Status:</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: '13px' }}>
          {filteredRegions.length} region
          {filteredRegions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="crud-table-wrap">
        <table className="crud-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRegions.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={5}
                  style={{ textAlign: 'center', padding: '32px', opacity: 0.5 }}
                >
                  No regions found
                </td>
              </tr>
            ) : (
              filteredRegions.map((region) => (
                <tr key={region.id}>
                  <td style={{ opacity: 0.5, fontSize: '13px' }}>
                    {region.id}
                  </td>
                  <td>
                    <div className="row-main-text">{region.name}</div>
                  </td>
                  <td>
                    <span
                      className={`crud-status-badge ${
                        region.is_active
                          ? 'crud-status-active'
                          : 'crud-status-expired'
                      }`}
                    >
                      {region.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ fontSize: '12px', opacity: 0.6 }}>
                    {region.created_at
                      ? new Date(region.created_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>
                    <div className="crud-actions">
                      <button
                        className="crud-action-button"
                        onClick={() => openEdit(region)}
                      >
                        Edit
                      </button>
                      <button
                        className="crud-action-button crud-action-danger"
                        onClick={() => handleDelete(region)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {renderPagination()}
      </div>

      {loading && <div className="crud-loading">Syncing...</div>}

      {/* Modal */}
      {isModalOpen &&
        createPortal(
          <div
            className="crud-modal-overlay"
            onMouseDown={(e) =>
              e.target === e.currentTarget && setIsModalOpen(false)
            }
          >
            <div className="crud-modal-panel">
              <div className="crud-modal-head">
                <h2>{editingId ? 'Edit' : 'Create'} Region</h2>
                <button
                  className="crud-modal-close"
                  onClick={() => setIsModalOpen(false)}
                >
                  X
                </button>
              </div>

              <form className="crud-form" onSubmit={handleSubmit}>
                {/* Name EN */}
                <label className="crud-field">
                  <span>Region Name</span>
                  <input
                    name="name"
                    value={formState.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Maadi"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && (
                    <span className="field-error">{errors.name}</span>
                  )}
                </label>

                {/* Status */}
                <label className="crud-field">
                  <span>Status</span>
                  <select
                    name="is_active"
                    value={formState.is_active}
                    onChange={handleInputChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </label>

                <div className="crud-modal-actions">
                  <button
                    type="button"
                    className="crud-action-button"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="crud-add-button">
                    {editingId ? 'Save Changes' : 'Create Region'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
};

export default Regions;
